// web-common/src/router.js — the tiny in-house history router (D1-DESIGN §1.1). The IA is
// three destinations + one linear flow; a routing dependency earns nothing here. Path
// patterns are literal segments + ':param'; longest exact match wins by ORDER (list specific
// routes first). SPA hosting rewrites ** -> /index.html, so deep links land here.
//
//   makeRouter(routes) + <RouterView/> — routes: [{ path:'/brands/:id', render:(params)=>jsx }]
//   navigate('/path')                  — pushState + notify
//   <Link to="/path">                  — an <a> that navigates without a reload
//   useRoute()                         — { path, params } of the current match

import React, { createContext, useContext, useEffect, useState } from 'react';

const RouterCtx = createContext(null);

export function matchPath(pattern, pathname) {
  const p = pattern.split('/').filter(Boolean);
  const s = pathname.split('/').filter(Boolean);
  if (p.length !== s.length) return null;
  const params = {};
  for (let i = 0; i < p.length; i++) {
    if (p[i].startsWith(':')) params[p[i].slice(1)] = decodeURIComponent(s[i]);
    else if (p[i] !== s[i]) return null;
  }
  return params;
}

export function navigate(path, { replace = false } = {}) {
  if (replace) history.replaceState(null, '', path);
  else history.pushState(null, '', path);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

export function Link({ to, children, ...rest }) {
  return React.createElement('a', {
    href: to,
    onClick: (e) => {
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return; // let the browser do browser things
      e.preventDefault();
      navigate(to);
    },
    ...rest,
  }, children);
}

export function Router({ routes, fallback = null, children }) {
  const [pathname, setPathname] = useState(window.location.pathname);
  useEffect(() => {
    const onPop = () => setPathname(window.location.pathname);
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);
  let match = null;
  for (const r of routes) {
    const params = matchPath(r.path, pathname);
    if (params) { match = { route: r, params, path: pathname }; break; }
  }
  const value = { pathname, match };
  const view = match ? match.route.render(match.params) : fallback;
  return React.createElement(RouterCtx.Provider, { value }, children ? children(view) : view);
}

export function useRoute() {
  const ctx = useContext(RouterCtx);
  return ctx ? { pathname: ctx.pathname, params: (ctx.match && ctx.match.params) || {} } : { pathname: '/', params: {} };
}
